import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { UPDATE_RESOURCE_CATEGORY } from '../../../gql/mutations/resourceCategoryMutations'
import { IUpdateResourceCategoryResponse, IResourceCategory } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'
import { flattenCategoryTree } from '../categoryTreeHelpers'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
})

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  categories: IResourceCategory[]
  category: IResourceCategory
}

export const ModalEditCategory: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  categories,
  category,
}) => {
  const [updateCategory, { data, loading, error }] =
    useMutation<IUpdateResourceCategoryResponse>(UPDATE_RESOURCE_CATEGORY)

  const formik = useFormik({
    initialValues: {
      name: category.name,
      parentId: category.parent?.id || '',
      order: category.order,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (v) => {
      try {
        await updateCategory({
          variables: {
            id: parseId(category.id),
            categoryData: {
              name: v.name,
              parentId: v.parentId ? parseId(v.parentId) : null,
              order: v.order,
            },
          },
        })
      } catch (err) {
        console.error('Error updating category:', err)
        toast.error('Error updating folder')
      }
    },
  })

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.updateResourceCategory.success) {
      onClose()
      refetch()
      toast.success('Folder updated successfully')
    } else if (
      data.updateResourceCategory.errors &&
      data.updateResourceCategory.errors.length > 0
    ) {
      toast.error(`Error: ${data.updateResourceCategory.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  // Excluye la propia categoría y su subárbol para evitar ciclos al mover
  const parentOptions = flattenCategoryTree(categories, category.id)
  const selectedParent = parentOptions.find((opt) => opt.value === formik.values.parentId) || null

  return (
    <Modal show={isOpen} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-pencil" /> Edit Folder
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.name && formik.touched.name)}
              autoFocus
            />
            {formik.errors.name && formik.touched.name && (
              <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Parent folder</Form.Label>
            <Select
              isClearable
              value={selectedParent}
              onChange={(option) => formik.setFieldValue('parentId', option?.value || '')}
              options={parentOptions}
              placeholder="None (root folder)"
            />
            <Form.Text className="text-muted">
              Change the parent to move this folder. A folder cannot be moved inside itself.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Display Order</Form.Label>
            <Form.Control
              type="number"
              name="order"
              value={formik.values.order}
              onChange={formik.handleChange}
              min="0"
            />
            <Form.Text className="text-muted">Lower numbers appear first</Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" /> Save Changes
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
